// Paleta por defecto . Sirve como respaldo cuando la empresa no
// definió un `colorPdf`. Los tonos NEUTRALES (texto/divisorias grises) NO se
// derivan del color elegido y se conservan siempre desde aquí.
const PDF_COLORS_BASE = {
  primary: "#2BA8C0",        // --logo-cyan: SOLO el wordmark/logo BIWO
  tableHeader: "#0098AA",    // --biwo-table-header: SOLO texto de los headers de la tabla de conceptos
  tableDivider: "#A2CED3",   // --biwo-table-divider: SOLO la línea que cierra la tabla de conceptos (1.5–2px)
  neutralDivider: "#BBBBBB", // --neutral-divider: TODAS las demás divisorias horizontales del documento (1px)
  title: "#111827",          // --text-title: título del documento ("Remisión" / "N° ...") casi negro
  divider: "#A8DCE6",   // --biwo-divider: divisorias internas de complementos (CFDI/pagaré/CE)
  ink: "#1F2937",       // --text-primary: cuerpo de texto, valores
  label: "#374151",     // --text-secondary: labels, metadatos
  muted: "#9CA3AF",     // --text-muted: texto bajo el QR, notas legales
  danger: "#CF1322",    // --text-danger: cifras que NO deberían salir (descuadres)
  warning: "#FFF7E6",   // --bg-warning: fondo de avisos que no se deben pasar por alto
  white: "#FFFFFF",     // --bg
  tint: "#EAF6F8",      // tono suave derivado de la marca (panel de TOTAL)

  // Aliases retro-compatibles (mapeados a la paleta BIWO)
  primaryDark: "#2BA8C0",
  secondary: "#A8DCE6",
  line: "#A8DCE6",
  lineStrong: "#A8DCE6",
  accentSoft: "#FFFFFF",
  zebra: null,
  text: "#1F2937",
  textSoft: "#374151",
  headerBg: "#FFFFFF",
};

export const PDF_COLORS = { ...PDF_COLORS_BASE };

// ── Utilidades de color (degradados sobre el color elegido) ────────
const clampByte = (n) => Math.max(0, Math.min(255, Math.round(n)));
const hexToRgb = (hex) => {
  const h = String(hex || "").trim().replace(/^#/, "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const int = parseInt(full, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};
const rgbToHex = ({ r, g, b }) =>
  "#" + [r, g, b].map((v) => clampByte(v).toString(16).padStart(2, "0")).join("");
// Mezcla un color hacia un objetivo (blanco para aclarar, negro para oscurecer).
const mixToward = (rgb, target, amount) => ({
  r: rgb.r + (target.r - rgb.r) * amount,
  g: rgb.g + (target.g - rgb.g) * amount,
  b: rgb.b + (target.b - rgb.b) * amount,
});
const WHITE_RGB = { r: 255, g: 255, b: 255 };
const BLACK_RGB = { r: 0, g: 0, b: 0 };
const lighten = (hex, amount) => {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHex(mixToward(rgb, WHITE_RGB, amount)) : hex;
};
const darken = (hex, amount) => {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHex(mixToward(rgb, BLACK_RGB, amount)) : hex;
};

/**
 * Construye la paleta de marca a partir del color de la empresa (`colorPdf`).
 * Los tonos que faltan se obtienen como degradados (más claros/oscuros) del
 * mismo color. Si no hay color válido, regresa la paleta base (fallback).
 * @param {object} empresa
 * @returns {object} paleta completa
 */
export const buildPdfColors = (empresa = {}) => {
  const base = empresa?.colorPdf;
  if (!hexToRgb(base)) return { ...PDF_COLORS_BASE };
  return {
    ...PDF_COLORS_BASE, // conserva neutrales (texto, grises) y aliases neutrales
    primary: base,
    primaryDark: darken(base, 0.18),
    tableHeader: darken(base, 0.1),
    tableDivider: lighten(base, 0.45),
    divider: lighten(base, 0.55),
    secondary: lighten(base, 0.55),
    line: lighten(base, 0.55),
    lineStrong: lighten(base, 0.35),
    tint: lighten(base, 0.88),
  };
};

/**
 * Recalcula la paleta viva PDF_COLORS in-place para la empresa dada. Debe
 * llamarse al inicio de cada generación de PDF v2 (antes de pdfStylesV2).
 * @param {object} empresa
 * @returns {object} PDF_COLORS ya actualizado
 */
export const applyPdfColors = (empresa = {}) => {
  Object.assign(PDF_COLORS, buildPdfColors(empresa));
  return PDF_COLORS;
};

// ── 2. ESPACIADO (sistema de 4px, en pt) ──────────────────────────
export const PDF_SPACE = { xs: 4, sm: 8, md: 16, lg: 24, xl: 40, xxl: 60 };

export const PDF_LINE_HEIGHT = 1.05;


// Escala del CUERPO del documento (encabezado, cliente, totales, sellos, pie,
// QR del kiosko, etc.). Bajarla compacta todo el texto salvo la tabla de
// insumos, que tiene su PROPIA escala (PDF_TABLE_FONT_SCALE) para conservar su
// tamaño/legibilidad. Antes el cuerpo iba a 1.15; se redujo a 1.0 (tamaños base)
// para aprovechar el espacio y que una factura de ≤5 insumos quepa en una hoja.
export const PDF_FONT_SCALE = 1.0;
// Escala EXCLUSIVA de la tabla de insumos: se mantiene en 1.15 para que la
// tabla NO se achique cuando se reduce el resto del documento.
export const PDF_TABLE_FONT_SCALE = 1.15;
// Aplica la escala del cuerpo a un tamaño base y redondea a 1 decimal.
export const pdfFz = (n) => Math.round(Number(n) * PDF_FONT_SCALE * 10) / 10;
// Igual, pero con la escala de la tabla de insumos.
export const pdfFzTabla = (n) => Math.round(Number(n) * PDF_TABLE_FONT_SCALE * 10) / 10;

// Familia tipográfica para las ETIQUETAS de los pares label/valor: Mulish
// SemiBold (600). Da un peso intermedio que distingue el campo de su valor
// (Mulish Regular) sin el contraste fuerte del Bold (700). La familia se
// registra en src/Pdf/PdfV2.0/mulishSemiBoldFont.js, importado por efecto
// secundario en las plantillas PDF v2 (remisionPdfV2 / facturaPdfV2).
export const PDF_LABEL_FONT = "MulishSemiBold";


export const PDF_PAGE_MARGINS = [38, 24, 38, 28];
export const PDF_CONTENT_WIDTH = 536; // 612 - 38*2


export const pdfStylesV2 = (empresa = {}) => {
  // Tamaños BASE (sin escalar). La escala global PDF_FONT_SCALE se aplica abajo.
  const styles = {
    // Bloques fuertes / encabezados de bloque
    header: { fontSize: 9, bold: true, color: PDF_COLORS.ink },
    // Labels / metadatos
    subheader: { fontSize: 7, color: PDF_COLORS.label },
    // Valores / cuerpo
    textoChico: { fontSize: 7, color: PDF_COLORS.ink },

    tableExample: { margin: [0, 4, 0, 4] },
    tablaSinMargen: { margin: [0, 0, 0, 0] },

    // Títulos compat
    tableHeaderIzquierda: { bold: true, fontSize: 20, color: PDF_COLORS.ink },
    tableHeader: { bold: true, fontSize: 10, color: PDF_COLORS.ink, alignment: "center" },

    textoAdicional: { fontSize: empresa.tamañoTextoAdicional || 7, color: PDF_COLORS.ink },
    textoAdicionalPedidosCotizaciones: { fontSize: empresa.tamañoTextoAdicionalPedidosCotizaciones || 7, color: PDF_COLORS.ink },
    textoChicoFirma: { bold: true, fontSize: 7, color: PDF_COLORS.ink },
    textoChicoData: { fontSize: 6, color: PDF_COLORS.muted },

    // Encabezados de columnas de tabla
    tituloTabla: { fontSize: 7, bold: true, color: PDF_COLORS.tableHeader, alignment: "center" },

    // Título de sección
    seccionTitulo: { fontSize: 9.5, bold: true, color: PDF_COLORS.ink },

    // Etiqueta fuerte (totales)
    etiquetaFuerte: { fontSize: 8.5, bold: true, color: PDF_COLORS.ink },

    tituloPagare: { fontSize: 12, bold: true, color: PDF_COLORS.ink, alignment: "center" },
    textoPagare: { fontSize: empresa.tamañoTextoPagare || 7.5, color: PDF_COLORS.ink },
    textoPagareFirma: { bold: true, fontSize: empresa.tamañoTextoPagare || 7.5, color: PDF_COLORS.ink },

    textoTabla: { fontSize: 7, italics: false, color: PDF_COLORS.ink },
    textoTablaChico: { fontSize: 5.5, italics: false, color: PDF_COLORS.ink }, // 1pt menos: evita que el "$" salte de línea
    descuentoPartida: { fontSize: 5, color: PDF_COLORS.muted },

    // TOTAL destacado
    totalDestacado: { fontSize: 11, bold: true, color: PDF_COLORS.ink },
  };

  // Escala de tipografía: el cuerpo usa PDF_FONT_SCALE; la tabla de insumos
  // (estos estilos) conserva su tamaño con PDF_TABLE_FONT_SCALE.
  const TABLE_STYLE_KEYS = new Set(["tituloTabla", "textoTabla", "textoTablaChico", "descuentoPartida"]);
  for (const [key, st] of Object.entries(styles)) {
    if (typeof st.fontSize === "number") {
      st.fontSize = TABLE_STYLE_KEYS.has(key) ? pdfFzTabla(st.fontSize) : pdfFz(st.fontSize);
    }
  }
  return styles;
};

// ── 3. LAYOUTS DE TABLA (sin bordes/zebra; separación por aire) ────
export const lineLayoutsV2 = {
  noBorders: "noBorders",

  // Caja con borde exterior sutil (uso puntual, p.ej. pagaré/equipo médico)
  cajaLimpia: {
    hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 1 : 0),
    vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length ? 1 : 0),
    hLineColor: () => PDF_COLORS.divider,
    vLineColor: () => PDF_COLORS.divider,
    paddingTop: () => 8,
    paddingBottom: () => 8,
    paddingLeft: () => 10,
    paddingRight: () => 10,
  },


  tablaConceptos: {
    fillColor: () => null,
    hLineWidth: (i, node) => {
      if (i === 1) return 1.5; // bajo el encabezado
      if (i === node.table.body.length) return 1.5; // pie de la tabla
      return 0;
    },
    hLineColor: () => PDF_COLORS.tableDivider,
    vLineWidth: () => 0,
    paddingTop: () => 9,
    paddingBottom: () => 9,
    paddingLeft: () => 6,
    paddingRight: () => 6,
  },

  rejilla: {
    fillColor: () => null,
    hLineWidth: (i, node) => {
      if (i === 0 || i === node.table.body.length) return 1;
      if (i === 1) return 1;
      return 0.4;
    },
    hLineColor: () => PDF_COLORS.divider,
    vLineWidth: () => 0,
    paddingTop: () => 4,
    paddingBottom: () => 4,
    paddingLeft: () => 5,
    paddingRight: () => 5,
  },

  // Sección con borde claro (uso puntual)
  seccion: {
    hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 1 : 0.4),
    vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length ? 1 : 0),
    hLineColor: () => PDF_COLORS.divider,
    vLineColor: () => PDF_COLORS.divider,
    paddingTop: () => 6,
    paddingBottom: () => 6,
    paddingLeft: () => 8,
    paddingRight: () => 8,
  },
};

/**
 * Une partes de una dirección en una sola cadena continua separada por comas
 * (estilo Google Maps). Ignora vacíos. REGLA GLOBAL de direcciones BIWO.
 * @param {Array<any>} parts
 * @returns {string}
 */
export const joinComma = (parts = []) =>
  parts
    .map((p) => (p === undefined || p === null ? "" : String(p).trim()))
    .filter((p) => p !== "")
    .join(", ");

/**
 * Línea divisoria horizontal 1px en --biwo-divider, ancho completo del contenido.
 * @param {string} color
 * @param {number} margin margen vertical (pt)
 * @param {number} lineWidth
 */
export const divisorV2 = (color = PDF_COLORS.divider, margin = 10, lineWidth = 1.5) => ({
  canvas: [{ type: "line", x1: 0, y1: 0, x2: PDF_CONTENT_WIDTH, y2: 0, lineWidth, lineColor: color }],
  margin: [0, margin, 0, margin],
});

/**
 * Título de sección (estilo Odoo): texto en tinta + una fina línea cian de
 * ancho completo debajo, como separador sobrio de sección.
 * @param {string} texto
 * @param {object} opts { alignment }
 * @returns nodo pdfMake (stack)
 */
export const seccionHeadingV2 = (texto, { alignment = "left" } = {}) => ({
  margin: [0, 7, 0, 3],
  stack: [
    { text: texto, style: "seccionTitulo", alignment },
    divisorV2(PDF_COLORS.divider, 3, 1),
  ],
});
